import { useState, useRef, useEffect } from "react";
import { MdCloudUpload } from "react-icons/md";
import { AiOutlineClose } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import ENDPOINTS from "../../api/endpoint";
import Loader from "../Loader";
import { showErrorToast, showSuccessToast } from "../Toast";
import {
  ALLOWED_ACCEPT,
  ALLOWED_EXTENSIONS,
  SUPPORTED_FORMATS_LABEL,
} from "../../constants/allowedFileTypes";

const MAX_TOTAL_SIZE_MB = 1024; // 1 GB
const MAX_TOTAL_SIZE_BYTES = MAX_TOTAL_SIZE_MB * 1024 * 1024;

export default function UploadModal({ isOpen, onClose, onUploadSuccess }) {
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // reset uploaded files when modal closes
  useEffect(() => {
    if (!isOpen) {
      setUploadedFiles([]);
    }
  }, [isOpen]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files?.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files) => {
    const newFiles = Array.from(files);
    setUploadedFiles((prev) => {
      const currentTotal = prev.reduce((sum, f) => sum + f.size, 0);
      const newFilesTotal = newFiles.reduce((sum, f) => sum + f.size, 0);
      if (currentTotal + newFilesTotal > MAX_TOTAL_SIZE_BYTES) {
        showErrorToast(
          `Total file size must not exceed 1 GB`
        );
        return prev;
      }
      return [...prev, ...newFiles];
    });
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const removeFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Upload multiple files with the name "file"
  const handleContinue = async () => {
    if (uploadedFiles.length === 0) {
      showErrorToast("Please upload at least one file");
      return;
    }

    // Validate file formats
    const invalidFiles = uploadedFiles.filter((file) => {
      const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
      return !ALLOWED_EXTENSIONS.includes(ext);
    });
    if (invalidFiles.length > 0) {
      const invalidNames = invalidFiles.map((f) => f.name).join(", ");
      showErrorToast(
        `Only ${SUPPORTED_FORMATS_LABEL} are allowed. Invalid file(s): ${invalidNames}`
      );
      return;
    }

    // Validate total size
    const totalSize = uploadedFiles.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > MAX_TOTAL_SIZE_BYTES) {
      showErrorToast(
        `Total file size must not exceed 1 GB. Current total: ${(totalSize / 1024 / 1024).toFixed(2)} MB`
      );
      return;
    }

    const user = JSON.parse(localStorage.getItem("user")) || {};
    const token = user.access_token;
    const companyId = user.company_id;
    const userId = user.user_id || user.id;

    if (!companyId || !token) {
      showErrorToast("Missing company or authentication details");
      return;
    }

    setLoading(true);
    onClose?.();

    try {
      const formData = new FormData();

      // Append all files with the same key "file"
      // This sends multiple files as: file, file, file...
      uploadedFiles.forEach((file) => {
        formData.append("files", file);
      });

      // Add user_id as Form field (required by backend)
      if (userId) {
        formData.append("user_id", String(userId));
      }

      // Debug: Log what's being sent (optional - remove in production)
      console.log("Uploading files:");
      for (let pair of formData.entries()) {
        console.log(`${pair[0]}: ${pair[1].name || pair[1]}`);
      }

      // Check if chatbot_id is present in localStorage
      const activeChatbotId = localStorage.getItem("active_chatbot_id");

      // Use chatbot-specific endpoint if chatbot_id is available, otherwise use regular endpoint
      const endpoint = activeChatbotId
        ? ENDPOINTS.UPLOAD_Multiple_DOCUMENT_CHATBOT(companyId, activeChatbotId)
        : ENDPOINTS.UPLOAD_Multiple_DOCUMENT(companyId);

      const headers = {
        Authorization: `Bearer ${token}`,
      };

      // Add X-User-Id header if userId is available
      if (userId) {
        headers["X-User-Id"] = String(userId);
      }

      const res = await fetch(endpoint, {
        method: "POST",
        headers,
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        showErrorToast(data.detail || "Upload failed");
        return;
      }

      const {
        successful_uploads = 0,
        failed_uploads = 0,
        results = [],
      } = data;

      const failedResults = results.filter((r) => !r.processed);
      const failedMessages = failedResults
        .map((r) => `${r.document_name}: ${r.message || r.error || "Upload failed"}`)
        .join(" • ");

      if (failed_uploads === 0) {
        // All succeeded: show only success count
        const msg =
          successful_uploads === 1
            ? "file uploaded successfully"
            : `${successful_uploads} files uploaded successfully`;
        showSuccessToast(msg);
      } else if (successful_uploads === 0) {
        // All failed: show error toast with failure messages
        showErrorToast(
          failedMessages || (failed_uploads === 1 ? "1 file failed" : `${failed_uploads} files failed`)
        );
      } else {
        // Partial: show success message with counts, then error toast with failed messages
        const successPart =
          successful_uploads === 1
            ? "1 file uploaded"
            : `${successful_uploads} files uploaded`;
        const failPart =
          failed_uploads === 1 ? "1 failed" : `${failed_uploads} failed`;
        showSuccessToast(`${successPart} and ${failPart}`);
        setTimeout(() => showErrorToast(failedMessages), 3000);
      }

      // Clear uploaded files after upload response
      setUploadedFiles([]);

      // Call the success callback if provided (to refresh parent component)
      if (onUploadSuccess) {
        onUploadSuccess();
      }

      navigate("/doc");
    } catch (error) {
      console.error("Upload error:", error);
      showErrorToast(error.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={onClose}
        >
          <div
            className="bg-white dark:bg-bg-dark rounded-2xl max-w-3xl w-full mx-2 sm:mx-4 relative p-4 sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`rounded-xl p-4 sm:p-10 text-center transition-colors border-2 border-dashed ${dragActive
                ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900"
                : "border-gray-300 "
                }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {uploadedFiles.length === 0 ? (
                <>
                  <div className="mb-4 sm:mb-6">
                    <MdCloudUpload className="w-12 h-12 sm:w-20 sm:h-20 text-indigo-900 dark:text-white mx-auto" />
                  </div>
                  <h3 className="text-base sm:text-lg md:text-3xl font-semibold text-gray-900 dark:text-white mb-2">
                    Choose a file or drag & drop it here
                  </h3>
                  <p className="text-sm sm:text-base md:text-xl text-gray-500 dark:text-gray-400 mb-4 sm:mb-6">
                    <span className="font-medium">{SUPPORTED_FORMATS_LABEL}</span>{" "}
                    (max size: 1GB)
                  </p>

                  <button
                    onClick={openFileDialog}
                    className="bg-indigo-900 dark:bg-white hover:bg-indigo-700 text-white dark:text-indigo-900 dark:hover:text-white dark:hover:bg-indigo-900 font-medium py-2 px-4 sm:py-3 sm:px-8 rounded-xl transition-colors text-sm sm:text-base"
                  >
                    Browse Files
                  </button>
                </>
              ) : (
                <div className="text-left">
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
                    Uploaded Files ({uploadedFiles.length})
                  </h4>
                  <ul className="space-y-2 max-h-48 sm:max-h-60 overflow-y-auto">
                    {uploadedFiles.map((file, index) => (
                      <li
                        key={index}
                        className="p-2 sm:p-3 bg-gray-100 dark:bg-bg-dark rounded-lg flex justify-between items-center"
                      >
                        <div className="flex-1 truncate min-w-0">
                          <span className="truncate dark:text-text-light block text-sm sm:text-base">
                            {file.name}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                        </div>
                        <button
                          onClick={() => removeFile(index)}
                          className="ml-2 sm:ml-3 text-gray-500 dark:text-text-light hover:text-red-600 flex-shrink-0"
                        >
                          <AiOutlineClose size={18} />
                        </button>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-0 sm:space-x-5 mt-3 sm:mt-4">
                    <button
                      onClick={openFileDialog}
                      className="bg-indigo-900 dark:bg-white hover:bg-indigo-700 text-white dark:text-indigo-900 dark:hover:text-white dark:hover:bg-indigo-900 font-medium py-2 px-4 sm:px-6 rounded-lg transition-colors text-sm sm:text-base w-full sm:w-auto"
                    >
                      Add More Files
                    </button>
                    <button
                      onClick={handleContinue}
                      className="bg-indigo-900 dark:bg-white hover:bg-indigo-700 text-white dark:text-indigo-900 dark:hover:text-white dark:hover:bg-indigo-900 font-medium py-2 px-4 sm:px-6 rounded-lg transition-colors text-sm sm:text-base w-full sm:w-auto"
                    >
                      Upload {uploadedFiles.length} File
                      {uploadedFiles.length > 1 ? "s" : ""}
                    </button>
                  </div>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ALLOWED_ACCEPT}
                onChange={handleFileInput}
                className="hidden"
              />
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Loader */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999]">
          <Loader />
        </div>
      )}
    </>
  );
}
